import { Link, Navigate, useNavigate } from "react-router-dom";
import { useSyncExternalStore, useState } from "react";

import { usePageTitle } from "@/hooks/usePageTitle";

import {
  clearAuthSession,
  getAuthSessionSnapshot,
  getContactFirstName,
  subscribeAuthSession,
} from "@/lib/auth/session";
import { getGlofoxApiReferenceHref } from "@/lib/portal/glofox-routes";
import { GlofoxNav } from "@/components/portal/GlofoxNav";

const API_KEY = "gf_sb_pk_7x2mN9qLrT4fWkAe8cJv";
const API_TOKEN = "gf_sb_sk_mP3nXhY6wBsD1jQz5tRu";

type SetupState = "idle" | "wizard" | "done";

function SandboxSidebar() {
  const navigate = useNavigate();

  function handleLogout() {
    clearAuthSession();
    navigate("/login");
  }

  return (
    <div className="dash-sidebar" data-testid="gf-sandbox-sidebar">
      <div className="sb-sect">Developer</div>
      <Link
        className="sb-link active"
        to="/glofox/sandbox"
        style={{ color: "var(--gf)" }}
        data-testid="gf-sandbox-sidebar-dashboard"
      >
        <i className="ti ti-layout-dashboard" /> Dashboard
      </Link>
      <button type="button" className="sb-link" data-testid="gf-sandbox-sidebar-sandbox">
        <i className="ti ti-box" /> Sandbox
      </button>
      <button type="button" className="sb-link" data-testid="gf-sandbox-sidebar-credentials">
        <i className="ti ti-key" /> Credentials
      </button>
      <button type="button" className="sb-link" data-testid="gf-sandbox-sidebar-request-logs">
        <i className="ti ti-activity" /> Request logs
      </button>
      <button type="button" className="sb-link" data-testid="gf-sandbox-sidebar-webhooks">
        <i className="ti ti-webhook" /> Webhooks
      </button>
      <div className="sb-sect">Docs</div>
      <Link className="sb-link" to="/glofox" data-testid="gf-sandbox-sidebar-overview">
        <i className="ti ti-home" /> Overview
      </Link>
      <Link
        className="sb-link"
        to="/glofox/authentication"
        data-testid="gf-sandbox-sidebar-authentication"
      >
        <i className="ti ti-lock" /> Authentication
      </Link>
      <Link
        className="sb-link"
        to="/glofox/common-concepts"
        data-testid="gf-sandbox-sidebar-common-concepts"
      >
        <i className="ti ti-topology-star" /> Common concepts
      </Link>
      <Link
        className="sb-link"
        to="/glofox/flows/lead-sale"
        data-testid="gf-sandbox-sidebar-lead-sale"
      >
        <i className="ti ti-user-plus" /> Lead capture and sale flow
      </Link>
      <a
        className="sb-link"
        href={getGlofoxApiReferenceHref()}
        data-testid="gf-sandbox-sidebar-api-reference"
      >
        <i className="ti ti-code" /> API Reference
      </a>
      <div className="sb-sect">Account</div>
      <button type="button" className="sb-link" data-testid="gf-sandbox-sidebar-settings">
        <i className="ti ti-settings" /> Settings
      </button>
      <button
        type="button"
        className="sb-link"
        onClick={handleLogout}
        data-testid="gf-sandbox-sidebar-logout"
      >
        <i className="ti ti-logout" /> Log out
      </button>
    </div>
  );
}

export default function Sandbox() {
  usePageTitle("Sandbox");
  const session = useSyncExternalStore(
    subscribeAuthSession,
    getAuthSessionSnapshot,
    getAuthSessionSnapshot,
  );
  const companyName = session?.user.company_name ?? "";
  const displayName = session
    ? getContactFirstName(session.user.contact_name)
    : "";

  const [setupState, setSetupState] = useState<SetupState>("idle");
  const [progress, setProgress] = useState(0);
  const [steps, setSteps] = useState([
    { id: 1, title: "Provisioning branch", desc: `Creating isolated sandbox branch for ${companyName}…`, state: "pending" as "pending" | "active" | "done" },
    { id: 2, title: "Generating credentials", desc: "Issuing API key and token pair for sandbox access…", state: "pending" as "pending" | "active" | "done" },
    { id: 3, title: "Seeding test data", desc: "Loading sample members, classes, and memberships…", state: "pending" as "pending" | "active" | "done" },
    { id: 4, title: "Configuring webhooks", desc: "Setting up default webhook endpoint listeners…", state: "pending" as "pending" | "active" | "done" },
  ]);
  const [keyRevealed, setKeyRevealed] = useState(false);
  const [tokenRevealed, setTokenRevealed] = useState(false);
  const [envTab, setEnvTab] = useState<"sandbox" | "prod">("sandbox");
  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  function startSetup() {
    setSetupState("wizard");
    setSteps((prev) => prev.map((s, i) => ({ ...s, state: i === 0 ? "active" : "pending" })));

    const delays = [900, 1800, 2800, 3600];
    const pcts = [25, 50, 75, 100];

    delays.forEach((delay, i) => {
      setTimeout(() => {
        setProgress(pcts[i]);
        setSteps((prev) =>
          prev.map((s, idx) => {
            if (idx < i) return { ...s, state: "done" };
            if (idx === i) return { ...s, state: "active" };
            return s;
          })
        );
        if (i === delays.length - 1) {
          setTimeout(() => {
            setSteps((prev) => prev.map((s) => ({ ...s, state: "done" })));
            setSetupState("done");
          }, 700);
        }
      }, delay);
    });
  }

  function copyVal(val: string, field: string) {
    navigator.clipboard.writeText(val).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    });
  }

  return (
    <>
      <GlofoxNav />
      <div className="dash-shell">
        <SandboxSidebar />
        <main id="main-content" className="dash-main">
          <div className="dash-header">
            <h1 data-testid="gf-sandbox-welcome">
              Welcome back, {displayName} 👋
            </h1>
            <p>
              {companyName} —{" "}
              <span style={{ color: "var(--gf-dark)", fontWeight: 600 }}>
                Sandbox environment
              </span>
            </p>
          </div>

          <div className="summary-grid" data-testid="gf-sandbox-summary">
            <div className="sum-card">
              <div className="sum-label">Sandbox status</div>
              <div className="sum-val" style={{ fontSize: 16 }}>
                {setupState === "done" ? "Active" : "Not set up"}
              </div>
              <div
                className="sum-badge"
                style={
                  setupState === "done"
                    ? { background: "var(--gf-light)", color: "var(--gf-dark)" }
                    : { background: "#FFF0F0", color: "#a32d2d" }
                }
              >
                <i
                  className={`ti ${setupState === "done" ? "ti-circle-filled" : "ti-circle"}`}
                  style={{ fontSize: 8 }}
                />{" "}
                {setupState === "done" ? "Live" : "Inactive"}
              </div>
            </div>
            <div className="sum-card">
              <div className="sum-label">Requests today</div>
              <div className="sum-val">{setupState === "done" ? "0" : "—"}</div>
              <div className="sum-sub">of 300 burst limit</div>
            </div>
            <div className="sum-card">
              <div className="sum-label">Rate limit</div>
              <div className="sum-val">{setupState === "done" ? "3" : "—"}</div>
              <div className="sum-sub">requests / second</div>
            </div>
            <div className="sum-card">
              <div className="sum-label">Environment</div>
              <div className="sum-val" style={{ fontSize: 16 }}>Sandbox</div>
              <div className="sum-sub">v2.0 — v2.3 API</div>
            </div>
          </div>

          {setupState === "idle" && (
            <div className="setup-card" data-testid="gf-sandbox-setup-card">
              <div className="setup-icon">
                <i className="ti ti-box" />
              </div>
              <h2 data-testid="gf-sandbox-setup-idle-title">Spin up your sandbox</h2>
              <p>
                Your account is ready. Create a sandbox environment to get your
                API credentials and start integrating with the Glofox platform.
              </p>
              <button
                className="btn btn-primary"
                onClick={startSetup}
                data-testid="gf-sandbox-create"
                style={{ fontSize: 14, padding: "11px 28px", marginTop: ".5rem" }}
              >
                <i className="ti ti-player-play" /> Create sandbox
              </button>
            </div>
          )}

          {setupState === "wizard" && (
            <div className="setup-card ready">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: ".25rem",
                }}
              >
                <h2 style={{ fontSize: 16, margin: 0 }} data-testid="gf-sandbox-setup-title">
                  Setting up your sandbox…
                </h2>
                <span
                  style={{ fontSize: 13, fontWeight: 700, color: "var(--gf-dark)" }}
                >
                  {progress}%
                </span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <div className="wizard-steps">
                {steps.map((s) => (
                  <div
                    key={s.id}
                    className={`wstep${s.state === "active" ? " active-step" : s.state === "done" ? " done-step" : ""}`}
                  >
                    <div className="wstep-num">
                      {s.state === "done" ? (
                        <i className="ti ti-check" style={{ fontSize: 12 }} />
                      ) : (
                        s.id
                      )}
                    </div>
                    <div>
                      <div className="wstep-title">{s.title}</div>
                      <div className="wstep-desc">{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {setupState === "done" && (
            <>
              <div style={{ marginTop: "1.25rem" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: ".75rem",
                  }}
                >
                  <h3 style={{ fontSize: 15, fontWeight: 700 }} data-testid="gf-sandbox-credentials-title">
                    Your sandbox credentials
                  </h3>
                  <div className="env-tabs" role="tablist" aria-label="Environment">
                    <button
                      type="button"
                      role="tab"
                      id="gf-sandbox-env-sandbox-tab"
                      aria-selected={envTab === "sandbox"}
                      aria-controls="gf-sandbox-credentials-panel"
                      className={`env-tab${envTab === "sandbox" ? " on" : ""}`}
                      onClick={() => setEnvTab("sandbox")}
                      data-testid="gf-sandbox-env-sandbox"
                    >
                      Sandbox
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="gf-sandbox-env-production-tab"
                      aria-selected={envTab === "prod"}
                      aria-controls="gf-sandbox-credentials-panel"
                      className={`env-tab${envTab === "prod" ? " on" : ""}`}
                      onClick={() => setEnvTab("prod")}
                      data-testid="gf-sandbox-env-production"
                    >
                      Production
                    </button>
                  </div>
                </div>

                <div className="callout warn" style={{ marginBottom: "1rem" }}>
                  <i className="ti ti-alert-triangle" />
                  <p data-testid="gf-sandbox-credentials-notice">
                    Keep these credentials secret. Never expose them in
                    client-side code or public repositories. Rotate immediately
                    if compromised.
                  </p>
                </div>

                <div className="creds-card" id="gf-sandbox-credentials-panel" role="tabpanel" aria-labelledby="gf-sandbox-env-sandbox-tab">
                  <div className="creds-row">
                    <span className="creds-key">Branch ID</span>
                    <div className="creds-val">
                      <code>branch_gf_sandbox_a3f9c2</code>
                      <button
                        className="copy-btn"
                        onClick={() => copyVal("branch_gf_sandbox_a3f9c2", "branch")}
                        data-testid="gf-sandbox-copy-branch"
                      >
                        <i className={`ti ${copiedField === "branch" ? "ti-check" : "ti-copy"}`} />
                        {copiedField === "branch" ? " Copied!" : " Copy"}
                      </button>
                    </div>
                  </div>
                  <div className="creds-row">
                    <span className="creds-key">API Key</span>
                    <div className="creds-val">
                      <code>
                        {keyRevealed ? (
                          API_KEY
                        ) : (
                          <span className="masked">•••••••••••••••••••••</span>
                        )}
                      </code>
                      {keyRevealed ? (
                        <button
                          className="copy-btn"
                          onClick={() => copyVal(API_KEY, "apikey")}
                          data-testid="gf-sandbox-copy-api-key"
                        >
                          <i className={`ti ${copiedField === "apikey" ? "ti-check" : "ti-copy"}`} />
                          {copiedField === "apikey" ? " Copied!" : " Copy"}
                        </button>
                      ) : (
                        <button
                          className="copy-btn"
                          onClick={() => setKeyRevealed(true)}
                          data-testid="gf-sandbox-reveal-api-key"
                        >
                          <i className="ti ti-eye" /> Reveal
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="creds-row">
                    <span className="creds-key">API Token</span>
                    <div className="creds-val">
                      <code>
                        {tokenRevealed ? (
                          API_TOKEN
                        ) : (
                          <span className="masked">•••••••••••••••••••••</span>
                        )}
                      </code>
                      {tokenRevealed ? (
                        <button
                          className="copy-btn"
                          onClick={() => copyVal(API_TOKEN, "apitoken")}
                          data-testid="gf-sandbox-copy-api-token"
                        >
                          <i className={`ti ${copiedField === "apitoken" ? "ti-check" : "ti-copy"}`} />
                          {copiedField === "apitoken" ? " Copied!" : " Copy"}
                        </button>
                      ) : (
                        <button
                          className="copy-btn"
                          onClick={() => setTokenRevealed(true)}
                          data-testid="gf-sandbox-reveal-api-token"
                        >
                          <i className="ti ti-eye" /> Reveal
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="creds-row">
                    <span className="creds-key">Base URL</span>
                    <div className="creds-val">
                      <code style={{ color: "#94E0CA" }}>
                        https://gf-api.aws.glofox.com/sandbox
                      </code>
                      <button
                        className="copy-btn"
                        onClick={() =>
                          copyVal(
                            "https://gf-api.aws.glofox.com/sandbox",
                            "baseurl"
                          )
                        }
                        data-testid="gf-sandbox-copy-base-url"
                      >
                        <i className={`ti ${copiedField === "baseurl" ? "ti-check" : "ti-copy"}`} />
                        {copiedField === "baseurl" ? " Copied!" : " Copy"}
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: ".75rem",
                    marginTop: "1.25rem",
                    flexWrap: "wrap",
                  }}
                >
                  <Link
                    className="btn btn-primary"
                    to="/glofox/authentication"
                    data-testid="gf-sandbox-cta-auth-docs"
                  >
                    <i className="ti ti-book" /> View authentication docs
                  </Link>
                  <Link
                    className="btn btn-outline"
                    to="/glofox/flows/lead-sale"
                    data-testid="gf-sandbox-cta-lead-sale"
                  >
                    <i className="ti ti-user-plus" /> Try the Lead capture and sale flow
                  </Link>
                  <a
                    className="btn btn-outline"
                    href={getGlofoxApiReferenceHref()}
                    data-testid="gf-sandbox-cta-api-reference"
                  >
                    <i className="ti ti-code" /> API Reference
                  </a>
                </div>
              </div>

              <div style={{ marginTop: "2rem" }}>
                <div className="activity-card" data-testid="gf-sandbox-activity">
                  <div className="activity-hdr">
                    <h3>Recent activity</h3>
                    <span style={{ fontSize: 12, color: "var(--t3)" }}>
                      Sandbox environment
                    </span>
                  </div>
                  {[
                    { color: "var(--gf)", msg: "Sandbox environment provisioned successfully", time: "Just now" },
                    { color: "var(--gf)", msg: "API credentials generated", time: "Just now" },
                    { color: "var(--gf)", msg: "Test data seeded — 12 members, 6 classes, 3 memberships", time: "Just now" },
                  ].map((row, i) => (
                    <div key={i} className="activity-row">
                      <div className="act-dot" style={{ background: row.color }} />
                      <div className="act-msg">{row.msg}</div>
                      <div className="act-time">{row.time}</div>
                    </div>
                  ))}
                  <div className="activity-row">
                    <div className="act-dot" style={{ background: "var(--t3)" }} />
                    <div className="act-msg">
                      Webhook listener configured at{" "}
                      <code
                        className="act-code"
                        style={{ background: "#EEF0F4", color: "var(--t1)" }}
                      >
                        https://localhost/webhooks
                      </code>
                    </div>
                    <div className="act-time">Just now</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </>
  );
}
